import {
  canPerformCapability,
  canPerformSpecCapability,
  type CapabilityName,
  type SpecCapabilityGroup,
  type WorkspacePersonaRole,
} from '@myplayer/rbac';
import type { OrganisationDocument } from '@myplayer/schema';
import {
  isSignInWithEmailLink,
  onAuthStateChanged,
  signInWithEmailLink,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { EMAIL_FOR_SIGN_IN_STORAGE_KEY, createFirebaseAuthActionHandlers } from './auth-actions.js';
import {
  ALL_WORKSPACES_SCOPE_VALUE,
  buildSessionFromClaims,
  canUseAllWorkspacesScope,
  computeAvailableWorkspaces,
  getWorkspaceScopeStorageKey,
  getWorkspaceStorageKey,
  resolveSwitchedPersonaSession,
  resolveSwitchedSession,
  type AdminUserSession,
  type WorkspaceOption,
  type WorkspaceScopeMode,
} from './auth-session.js';
import {
  callActivateInvitedMembershipFunction,
  getFirebaseClients,
  isGoogleAuthEnabled,
  isProductionBuild,
} from './firebase.js';
import { mapSnapshotDocToOrganisation } from './organisation-firestore.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';

export {
  ALL_WORKSPACES_SCOPE_VALUE,
  buildSessionFromClaims,
  canUseAllWorkspacesScope,
  computeAvailableWorkspaces,
  getWorkspaceScopeStorageKey,
  getWorkspaceStorageKey,
  resolveSwitchedPersonaSession,
  resolveSwitchedSession,
  type AdminUserSession,
  type WorkspaceOption,
  type WorkspaceScopeMode,
};

export interface AuthContextValue {
  readonly session: AdminUserSession | null;
  readonly organisation: OrganisationDocument | null;
  readonly setOrganisation?: (org: OrganisationDocument) => void;
  readonly availableWorkspaces: readonly WorkspaceOption[];
  readonly workspaceScope: WorkspaceScopeMode;
  readonly setWorkspaceScope: (scope: WorkspaceScopeMode) => void;
  readonly loading: boolean;
  readonly error: string | null;
  readonly emailLinkSent: boolean;
  readonly passwordResetSent: boolean;
  readonly verificationEmailSent: boolean;
  readonly googleAuthEnabled: boolean;
  readonly signInWithGoogle: () => Promise<void>;
  readonly sendEmailLink: (email: string) => Promise<void>;
  readonly sendPasswordReset: (email: string) => Promise<void>;
  readonly signInWithPassword: (email: string, password: string) => Promise<void>;
  readonly changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  readonly verifyResetCode: (oobCode: string) => Promise<string>;
  readonly confirmPasswordResetAction: (oobCode: string, newPassword: string) => Promise<void>;
  readonly applyEmailVerificationCode: (oobCode: string) => Promise<void>;
  readonly sendVerificationEmail: () => Promise<void>;
  readonly signOut: () => Promise<void>;
  readonly switchWorkspace: (workspaceId: string) => void;
  readonly switchPersonaRole: (personaRole: WorkspacePersonaRole) => void;
  readonly can: (capability: CapabilityName) => boolean;
  readonly canSpec: (capability: SpecCapabilityGroup) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  readonly children: ReactNode;
  readonly initialSession?: AdminUserSession | null;
  readonly initialOrganisation?: Partial<OrganisationDocument> | null;
  readonly initialWorkspaces?: readonly WorkspaceOption[];
  readonly initialWorkspaceScope?: WorkspaceScopeMode;
  readonly initialLoading?: boolean;
  readonly googleAuthEnabled?: boolean;
  readonly onSignInWithGoogle?: () => Promise<void>;
  readonly onSignInWithPassword?: (email: string, password: string) => Promise<void>;
  readonly onSendPasswordReset?: (email: string) => Promise<void>;
  readonly onSendEmailLink?: (email: string) => Promise<void>;
  readonly onChangePassword?: (currentPassword: string, newPassword: string) => Promise<void>;
  readonly onVerifyResetCode?: (oobCode: string) => Promise<string>;
  readonly onConfirmPasswordReset?: (oobCode: string, newPassword: string) => Promise<void>;
  readonly onApplyEmailVerificationCode?: (oobCode: string) => Promise<void>;
  readonly onSendVerificationEmail?: () => Promise<void>;
}

export function AuthProvider(props: AuthProviderProps): ReactElement {
  const {
    children,
    initialSession,
    initialOrganisation,
    initialWorkspaces,
    initialWorkspaceScope,
    initialLoading = false,
    googleAuthEnabled: googleAuthEnabledProp,
  } = props;
  const isControlled = initialSession !== undefined;
  const [session, setSession] = useState<AdminUserSession | null>(initialSession ?? null);
  const [organisation, setOrganisation] = useState<OrganisationDocument | null>(() => {
    if (initialOrganisation === null) return null;
    if (initialOrganisation !== undefined) {
      const orgId = initialOrganisation.orgId ?? initialSession?.orgId ?? 'org_default';
      return mapSnapshotDocToOrganisation(orgId, initialOrganisation, orgId);
    }
    return initialSession
      ? mapSnapshotDocToOrganisation(initialSession.orgId, undefined, initialSession.orgId)
      : null;
  });
  const [workspaceScopeState, setWorkspaceScopeState] = useState<WorkspaceScopeMode>(
    initialWorkspaceScope ?? 'WORKSPACE',
  );
  const [firestoreWorkspaces, setFirestoreWorkspaces] = useState<
    ReadonlyMap<
      string,
      { readonly name: string; readonly status?: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED' }
    >
  >(new Map());
  const [loading, setLoading] = useState<boolean>(isControlled ? initialLoading : true);
  const [error, setError] = useState<string | null>(null);
  const [emailLinkSent, setEmailLinkSent] = useState<boolean>(false);
  const [passwordResetSent, setPasswordResetSent] = useState<boolean>(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState<boolean>(false);
  const googleAuthEnabled = googleAuthEnabledProp ?? isGoogleAuthEnabled();

  useEffect(() => {
    if (isControlled) {
      setSession(initialSession ?? null);
      setLoading(initialLoading);
      return;
    }
    const { auth } = getFirebaseClients();
    if (typeof window !== 'undefined' && isSignInWithEmailLink(auth, window.location.href)) {
      const storedEmail = window.localStorage.getItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY);
      if (storedEmail) {
        signInWithEmailLink(auth, storedEmail, window.location.href)
          .then(() => window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY))
          .catch((err: unknown) =>
            setError(formatPlainAdminError(err, ADMIN_STRINGS.errors.signInEmailLinkFallback)),
          );
      }
    }
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setSession(null);
        setLoading(false);
        return;
      }
      try {
        const tokenResult = await firebaseUser.getIdTokenResult();
        let builtSession = buildSessionFromClaims(firebaseUser, tokenResult.claims);
        if (typeof window !== 'undefined') {
          const savedWs = window.localStorage.getItem(
            getWorkspaceStorageKey(builtSession.uid, builtSession.orgId),
          );
          if (savedWs?.trim()) {
            try {
              builtSession = resolveSwitchedSession(builtSession, savedWs);
            } catch {
              // Ignore stale stored workspace if user no longer has access
            }
          }
          const savedScope = window.localStorage.getItem(
            getWorkspaceScopeStorageKey(builtSession.uid, builtSession.orgId),
          );
          if (savedScope === 'ALL' || savedScope === 'WORKSPACE') {
            setWorkspaceScopeState(savedScope);
          }
        }
        setSession(builtSession);
        void callActivateInvitedMembershipFunction({ orgId: builtSession.orgId }).catch(() => {});
      } catch (err: unknown) {
        setError(formatPlainAdminError(err, 'We could not load your workspace permissions.'));
        setSession(null);
      } finally {
        setLoading(false);
      }
    });
  }, [isControlled, initialSession, initialLoading]);

  useEffect(() => {
    if (!session) {
      setOrganisation(null);
      return;
    }
    if (initialOrganisation !== undefined) {
      setOrganisation(
        initialOrganisation === null
          ? null
          : mapSnapshotDocToOrganisation(session.orgId, initialOrganisation, session.orgId),
      );
      return;
    }
    if (isControlled) {
      setOrganisation((prev) =>
        prev?.orgId === session.orgId
          ? prev
          : mapSnapshotDocToOrganisation(session.orgId, undefined, session.orgId),
      );
      return;
    }
    const { db } = getFirebaseClients();
    return onSnapshot(
      doc(db, 'organisations', session.orgId),
      (snap) =>
        setOrganisation(
          mapSnapshotDocToOrganisation(
            session.orgId,
            snap.exists() ? (snap.data() as Partial<OrganisationDocument>) : undefined,
            session.orgId,
          ),
        ),
      () => setOrganisation(mapSnapshotDocToOrganisation(session.orgId, undefined, session.orgId)),
    );
  }, [isControlled, session?.orgId, initialOrganisation]);

  useEffect(() => {
    if (isControlled || initialWorkspaces !== undefined || !session) return;
    if (!canPerformCapability(session.callerIdentity, 'workspace:create', session.orgId)) return;
    const { db } = getFirebaseClients();
    return onSnapshot(
      collection(db, 'organisations', session.orgId, 'workspaces'),
      (snap) => {
        const nextMap = new Map<
          string,
          { readonly name: string; readonly status?: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED' }
        >();
        for (const docSnap of snap.docs) {
          const data = docSnap.data();
          const status = data['status'];
          if (status === 'ARCHIVED') continue;
          const name =
            typeof data['name'] === 'string' && data['name'].trim().length > 0
              ? data['name'].trim()
              : docSnap.id;
          nextMap.set(docSnap.id, {
            name,
            ...(status === 'ACTIVE' || status === 'SUSPENDED' ? { status } : {}),
          });
        }
        setFirestoreWorkspaces(nextMap);
      },
      () => {},
    );
  }, [isControlled, session?.orgId, session?.callerIdentity.role, initialWorkspaces]);

  const availableWorkspaces = useMemo<readonly WorkspaceOption[]>(
    () => initialWorkspaces ?? computeAvailableWorkspaces(session, firestoreWorkspaces),
    [session, firestoreWorkspaces, initialWorkspaces],
  );

  const value = useMemo<AuthContextValue>(() => {
    const actionHandlers = createFirebaseAuthActionHandlers({
      ...props,
      isControlled,
      session,
      onMarkEmailVerified: () =>
        setSession((prev) => (prev ? { ...prev, emailVerified: true } : prev)),
      onMarkVerificationEmailSent: setVerificationEmailSent,
      onMarkEmailLinkSent: setEmailLinkSent,
      onMarkPasswordResetSent: setPasswordResetSent,
      onClearError: () => setError(null),
    });

    const signOut = async (): Promise<void> => {
      setError(null);
      if (!isControlled) await firebaseSignOut(getFirebaseClients().auth);
      setSession(null);
    };

    const setWorkspaceScope = (scope: WorkspaceScopeMode): void => {
      if (!session) return;
      if (scope === 'ALL' && !canUseAllWorkspacesScope(session, availableWorkspaces)) return;
      setWorkspaceScopeState(scope);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(getWorkspaceScopeStorageKey(session.uid, session.orgId), scope);
      }
    };

    const switchWorkspace = (workspaceId: string): void => {
      if (!session) return;
      setError(null);
      if (workspaceId === ALL_WORKSPACES_SCOPE_VALUE) {
        if (!canUseAllWorkspacesScope(session, availableWorkspaces)) {
          setError('You do not have permission to view all workspaces in this organisation.');
          return;
        }
        setWorkspaceScope('ALL');
        return;
      }
      try {
        const nextSession = resolveSwitchedSession(session, workspaceId, availableWorkspaces);
        setSession(nextSession);
        setWorkspaceScopeState('WORKSPACE');
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(
            getWorkspaceStorageKey(nextSession.uid, nextSession.orgId),
            nextSession.activeWorkspaceId,
          );
          window.localStorage.setItem(
            getWorkspaceScopeStorageKey(nextSession.uid, nextSession.orgId),
            'WORKSPACE',
          );
        }
      } catch (err: unknown) {
        setError(formatPlainAdminError(err, 'We could not switch to that workspace.'));
      }
    };

    const switchPersonaRole = (personaRole: WorkspacePersonaRole): void => {
      if (!session || isProductionBuild()) return;
      setError(null);
      const nextSession = resolveSwitchedPersonaSession(session, personaRole);
      setSession(nextSession);
      if (!canUseAllWorkspacesScope(nextSession, availableWorkspaces)) {
        setWorkspaceScopeState('WORKSPACE');
      }
    };

    const can = (capability: CapabilityName): boolean =>
      session
        ? canPerformCapability(
            session.callerIdentity,
            capability,
            session.orgId,
            session.activeWorkspaceId,
          )
        : false;

    const canSpec = (capability: SpecCapabilityGroup): boolean =>
      session
        ? canPerformSpecCapability(
            session.callerIdentity,
            capability,
            session.orgId,
            session.activeWorkspaceId,
          )
        : false;

    const effectiveWorkspaceScope: WorkspaceScopeMode =
      workspaceScopeState === 'ALL' && canUseAllWorkspacesScope(session, availableWorkspaces)
        ? 'ALL'
        : 'WORKSPACE';

    return {
      session,
      organisation,
      setOrganisation,
      availableWorkspaces,
      workspaceScope: effectiveWorkspaceScope,
      setWorkspaceScope,
      loading,
      error,
      emailLinkSent,
      passwordResetSent,
      verificationEmailSent,
      googleAuthEnabled,
      ...actionHandlers,
      signOut,
      switchWorkspace,
      switchPersonaRole,
      can,
      canSpec,
    };
  }, [
    session,
    organisation,
    availableWorkspaces,
    workspaceScopeState,
    loading,
    error,
    emailLinkSent,
    passwordResetSent,
    verificationEmailSent,
    googleAuthEnabled,
    isControlled,
    props,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
